/**
 * Centralized Permission Configuration (Frontend)
 * 
 * This must stay in sync with backend config/permissions.js
 * Defines permission keys and their mapping to routes/sidebar items.
 */

export const PERMISSIONS = {
  dashboard: {
    key: 'dashboard',
    label: 'Dashboard',
    description: 'Access to main dashboard overview',
    category: 'general',
  },
  posts: {
    key: 'posts',
    label: 'Blog Posts',
    description: 'Create, edit, and manage blog posts',
    category: 'content',
  },
  pages: {
    key: 'pages',
    label: 'Pages',
    description: 'Create and manage static pages',
    category: 'content',
  },
  media_coverage: {
    key: 'media_coverage',
    label: 'Media Coverage',
    description: 'Manage media coverage articles',
    category: 'content',
  },
  press_releases: {
    key: 'press_releases',
    label: 'Press Releases',
    description: 'Manage press release articles',
    category: 'content',
  },
  events: {
    key: 'events',
    label: 'Events',
    description: 'Create and manage events',
    category: 'content',
  },
  galleries: {
    key: 'galleries',
    label: 'Galleries',
    description: 'Manage photo galleries',
    category: 'content',
  },
  videos: {
    key: 'videos',
    label: 'Videos',
    description: 'Manage video content',
    category: 'content',
  },
  issues: {
    key: 'issues',
    label: 'Issues Management',
    description: 'Manage political issues and topics',
    category: 'management',
  },
  polls: {
    key: 'polls',
    label: 'Polls Management',
    description: 'Create and manage polls',
    category: 'management',
  },
  users: {
    key: 'users',
    label: 'User Management',
    description: 'Manage registered users',
    category: 'administration',
  },
  staff: {
    key: 'staff',
    label: 'Staff Management',
    description: 'Manage staff members and their permissions',
    category: 'administration',
  },
  volunteers: {
    key: 'volunteers',
    label: 'Volunteer Submissions',
    description: 'View and manage volunteer applications',
    category: 'management',
  },
  vehicles: {
    key: 'vehicles',
    label: 'Vehicle Management',
    description: 'Manage vehicles and political events',
    category: 'management',
  },
  groups: {
    key: 'groups',
    label: 'Groups & Messaging',
    description: 'Access groups and group messaging',
    category: 'general',
  },
  social_media: {
    key: 'social_media',
    label: 'Social Media Management',
    description: 'Manage social media accounts and posts',
    category: 'management',
  },
  settings: {
    key: 'settings',
    label: 'Website Settings',
    description: 'Manage website configuration and settings',
    category: 'administration',
  },
  sliders: {
    key: 'sliders',
    label: 'Sliders',
    description: 'Manage homepage sliders',
    category: 'content',
  },
};

export const ALL_PERMISSION_KEYS = Object.keys(PERMISSIONS);

// Permission categories for grouping in UI
export const PERMISSION_CATEGORIES = {
  general: { label: 'General', order: 1 },
  content: { label: 'Content Management', order: 2 },
  management: { label: 'Operations & Management', order: 3 },
  administration: { label: 'Administration', order: 4 },
};

/**
 * Map route paths to permission keys
 * Used by sidebar and PermissionGuard to determine which permission is needed
 */
export const ROUTE_PERMISSION_MAP = {
  '/dashboard': 'dashboard',
  '/dashboard/content/blog': 'posts',
  '/dashboard/content/page': 'pages',
  '/dashboard/content/media-coverage': 'media_coverage',
  '/dashboard/content/press-release': 'press_releases',
  '/dashboard/content/events': 'events',
  '/dashboard/content/gallery': 'galleries',
  '/dashboard/content/video': 'videos',
  '/dashboard/issues': 'issues',
  '/dashboard/polls': 'polls',
  '/dashboard/users': 'users',
  '/dashboard/staff': 'staff',
  '/dashboard/volunteer-submissions': 'volunteers',
  '/dashboard/vehicle-management': 'vehicles',
  '/dashboard/groups': 'groups',
  '/dashboard/social-media': 'social_media',
  '/dashboard/settings': 'settings',
  '/dashboard/sliders': 'sliders',
  '/dashboard/menus': 'settings',
  '/dashboard/profile-setting': 'dashboard', // profile is always accessible
};

/**
 * Get the permission key needed for a given route path
 */
export const getPermissionForRoute = (pathname) => {
  // Exact match first
  if (ROUTE_PERMISSION_MAP[pathname]) {
    return ROUTE_PERMISSION_MAP[pathname];
  }

  // Check prefix match (for nested routes like /dashboard/content/blog/create)
  const sortedRoutes = Object.keys(ROUTE_PERMISSION_MAP)
    .filter((r) => r !== '/dashboard') // Don't match base dashboard for sub-routes
    .sort((a, b) => b.length - a.length); // longest first

  for (const route of sortedRoutes) {
    if (pathname.startsWith(route + '/') || pathname === route) {
      return ROUTE_PERMISSION_MAP[route];
    }
  }

  // Default: require dashboard permission (basic access)
  return 'dashboard';
};

/**
 * Get permissions grouped by category for UI
 */
export const getPermissionsByCategory = () => {
  const grouped = {};

  for (const [key, perm] of Object.entries(PERMISSIONS)) {
    const cat = perm.category;
    if (!grouped[cat]) {
      grouped[cat] = {
        ...PERMISSION_CATEGORIES[cat],
        permissions: [],
      };
    }
    grouped[cat].permissions.push(perm);
  }

  return Object.entries(grouped)
    .sort(([, a], [, b]) => a.order - b.order)
    .map(([key, value]) => ({ key, ...value }));
};

/**
 * Default permissions per role (for offline/fallback use)
 * Admin: all permissions | Member: dashboard + groups | Staff: dashboard only
 */
export const DEFAULT_PERMISSIONS = {
  admin: ALL_PERMISSION_KEYS,
  member: ['dashboard', 'groups'],
  staff: ['dashboard'],
};
